import { useEffect, useMemo, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { resolveHref } from '../utils/resolveHref';
import { useCtaColorVars } from '../hooks/useCtaColorVars';
import { onColorLinksCss } from '../utils/onColorLinks';

// ============================================================================
// Types & helpers
// ============================================================================

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

/** A bedroom category derived from the floorplans collection. */
interface BedroomCategory {
  key: string;
  label: string;
  param: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

/** Map a raw bedroom count ("0", "2", "studio", …) to a category key. */
function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  return BED_ORDER[num] || `${num}`;
}

/** Human-readable label for a category key. */
function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bedroom',
    two: '2 Bedroom',
    three: '3 Bedroom',
    four: '4 Bedroom',
    five: '5 Bedroom',
  };
  return labels[key] || `${key} Bedroom`;
}

/** Group the floorplans collection into ordered bedroom categories. */
function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const keys = new Set<string>();
  for (const plan of floorplans) {
    keys.add(bedCountToKey(plan.floorplan_beds_min));
  }

  return Array.from(keys)
    .sort((a, b) => {
      const ai = BED_ORDER.indexOf(a);
      const bi = BED_ORDER.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    })
    .map((key) => ({ key, label: keyToLabel(key), param: key }));
}

// Shown on the company theme (and any property without floor plans) so the
// section always renders the four representative unit types.
const FALLBACK_CATEGORIES: BedroomCategory[] = [
  { key: 'studio', label: 'Studio', param: 'studio' },
  { key: 'one', label: '1 Bedroom', param: 'one' },
  { key: 'two', label: '2 Bedroom', param: 'two' },
  { key: 'three', label: '3 Bedroom', param: 'three' },
];

/** Derive the circular badge label (S / 1 / 2 / …) from a category key. */
function badgeForKey(key: string, index: number): string {
  if (key === 'studio') return 'S';
  const ordered = BED_ORDER.indexOf(key);
  if (ordered > 0) return String(ordered);
  const numeric = key.match(/\d+/);
  if (numeric) return numeric[0];
  return String(index + 1);
}

/** Abstract line-art floor plan glyph, open at the top-right for the badge. */
function FloorPlanIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 120 108"
      fill="none"
      stroke="currentColor"
      strokeWidth="6"
      strokeLinecap="square"
      strokeLinejoin="miter"
      className={className}
      aria-hidden="true"
    >
      {/* Outer walls — open at the top-right corner where the badge sits */}
      <polyline points="80,6 6,6 6,102 114,102 114,52" />
      {/* Top-left room */}
      <polyline points="6,42 46,42 46,6" />
      {/* Central dividing walls */}
      <polyline points="46,42 46,70 84,70 84,102" />
      {/* Right room wall */}
      <line x1="114" y1="70" x2="84" y2="70" />
    </svg>
  );
}

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

// Full-bleed angled section photo (gallery-pickable in the editor).
const backgroundDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Apartment interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a furnished apartment interior photo for the full-bleed section background. Prefer a wide living-room or open living/dining view with staged furniture. Weight furnished/staged cues higher (furnished, staged, model, sofa, couch, bed, decor, furniture). Skip floor plans, site maps, diagrams, empty/unfurnished rooms, bathrooms, laundry, closets, exteriors, and amenity/common-area photos. Do not reuse floorplanslist.feature_image.',
    context:
      'Full-bleed angled background behind the Floor Plans section on the home page, under a heavy primary-color overlay. Must be a different staged unit interior than floorplanslist.feature_image.',
  },
};

const featureImageDef: SlotGroupDefinition = {
  label: 'Feature Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a furnished apartment interior photo for the inset feature panel. Prefer a distinct room from the section background — e.g. if the background is a living room, prefer kitchen, bedroom, or dining (or the reverse). Weight furnished/staged cues higher (furnished, staged, model, sofa, couch, bed, decor, furniture). Skip floor plans, site maps, diagrams, empty/unfurnished rooms, bathrooms, laundry, closets, exteriors, and amenity/common-area photos. Do not reuse floorplanslist.background.',
    context:
      'Inset feature image beside the floor-plan copy in the Floor Plans section on the home page (desktop). Must be a different staged unit interior than floorplanslist.background.',
  },
};

const featureCtaDef: SlotGroupDefinition = {
  label: 'Feature CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/floorplans' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Explore Now' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Scroll reveal — slide elements up into view as they enter the viewport
// ============================================================================

/**
 * Observes descendants marked with `data-reveal` and toggles the `is-visible`
 * class as each scrolls into view (one-shot). Falls back to showing everything
 * immediately when IntersectionObserver is unavailable (e.g. during SSG) or the
 * user prefers reduced motion.
 */
function useScrollReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const els = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'));
    if (!els.length) return;

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion || typeof IntersectionObserver === 'undefined') {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' },
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return ref;
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const revealRef = useScrollReveal<HTMLDivElement>();
  const background = useSlotGroup('floorplanslist.background', backgroundDef);
  const featureImage = useSlotGroup('floorplanslist.feature_image', featureImageDef);
  const featureCta = useSlotGroup('floorplanslist.feature_cta', featureCtaDef);
  const ctaColorVars = useCtaColorVars();

  // Derive bedroom categories from the floorplans collection (grouped by bed
  // count), falling back to representative unit types when none are present.
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });
  const categories = useMemo(() => {
    const grouped = floorplans?.length ? groupFloorplansByBeds(floorplans) : [];
    return (grouped.length > 0 ? grouped : FALLBACK_CATEGORIES).slice(0, 4);
  }, [floorplans]);
  const threeUp = categories.length === 3;

  return (
    <section className="relative overflow-hidden bg-[var(--vibe-color-background)]">
      {/* Full-section property image, clipped to angled edges */}
      <div className="fp-section-image absolute inset-0">
        <EditableSlotGroup groupPath="floorplanslist.background" definition={backgroundDef}>
          <div className="absolute inset-0">
            {background.image ? (
              <VibesitesImage
                url={background.image}
                alt={background.alt || 'Apartment interior'}
                sizes="100vw"
                className="absolute inset-0 h-full w-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
            )}
            {/* 77% primary-color overlay — inside the slot group so the editor
                hit target still covers the full background. */}
            <div className="absolute inset-0 bg-[var(--vibe-color-primary)] opacity-[0.77]" />
          </div>
        </EditableSlotGroup>
      </div>

      {/* Content flows top→bottom inside the clipped photo. Top safe spacer keeps
          icons below the upper diagonal; the feature image sits last so only it
          can overhang the lower diagonal — never the copy or CTA. */}
      <div
        ref={revealRef}
        className="relative z-10 mx-auto flex w-full max-w-6xl flex-col items-center gap-10 px-5 pb-0 pt-0 sm:gap-12 sm:px-6 lg:gap-14"
      >
        {/* Matches the top clip inset so icons stay on the photo (esp. top-right). */}
        <div className="fp-clip-top-safe w-full shrink-0" aria-hidden="true" />

        {/* Single row — sized up when there are three icons, compact for four. */}
        <div
          className={
            threeUp
              ? 'grid w-full grid-cols-3 gap-x-4 gap-y-6 sm:gap-x-8 md:gap-x-10 lg:gap-14 xl:gap-16'
              : 'grid w-full grid-cols-4 gap-x-2 gap-y-6 sm:gap-x-4 md:gap-x-6 lg:gap-10 xl:gap-12'
          }
        >
          {categories.map((category, index) => {
            const name = category.label;
            const badge = badgeForKey(category.key, index);
            const url = `/floor-plans?bedroom=${category.param}`;

            return (
              <a
                key={category.key}
                href={url}
                data-reveal
                style={{ transitionDelay: `${index * 100}ms` }}
                className="fp-reveal flex min-w-0 flex-col items-center text-center transition-transform hover:scale-[1.03] lg:hover:scale-105"
              >
                <div
                  className={
                    threeUp
                      ? 'relative w-[min(5.5rem,100%)] sm:w-24 md:w-28 lg:w-36 xl:w-44'
                      : 'relative w-[min(3.5rem,100%)] sm:w-16 md:w-[4.5rem] lg:w-28 xl:w-36'
                  }
                >
                  <FloorPlanIcon className="h-auto w-full text-white drop-shadow-md" />
                  <span
                    className={
                      threeUp
                        ? 'absolute -top-2 right-0 flex h-8 w-8 items-center justify-center rounded-full bg-white text-base font-normal text-[var(--vibe-color-primary)] shadow-md sm:-top-2.5 sm:h-10 sm:w-10 sm:text-xl md:h-11 md:w-11 md:text-2xl lg:-top-3 lg:h-14 lg:w-14 lg:text-3xl xl:h-16 xl:w-16 xl:text-4xl'
                        : 'absolute -top-1.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-white text-xs font-normal text-[var(--vibe-color-primary)] shadow-md sm:-top-2 sm:h-7 sm:w-7 sm:text-sm md:h-8 md:w-8 md:text-base lg:-top-2.5 lg:h-12 lg:w-12 lg:text-2xl xl:h-14 xl:w-14 xl:text-3xl'
                    }
                    style={{ fontFamily: 'var(--vibe-font-heading)' }}
                  >
                    {badge}
                  </span>
                </div>
                <span
                  className={
                    threeUp
                      ? 'mt-3.5 max-w-full text-xs font-normal uppercase leading-tight tracking-wide text-white drop-shadow-sm sm:mt-4 sm:text-sm md:text-base lg:mt-5 lg:text-xl xl:text-2xl'
                      : 'mt-2.5 max-w-full text-[0.625rem] font-normal uppercase leading-tight tracking-wide text-white drop-shadow-sm sm:mt-3 sm:text-xs md:text-sm lg:mt-5 lg:text-lg xl:text-xl'
                  }
                  style={{ fontFamily: 'var(--vibe-font-heading)' }}
                >
                  {name}
                </span>
              </a>
            );
          })}
        </div>

        {/* Text stays on the photo. Feature image is desktop-only so tablet/mobile
            keep copy + CTA clear of the angled edge; at lg+ image sits right and overhangs.
            Extra bottom padding on the text column keeps the CTA above the lower diagonal. */}
        <div className="grid w-full items-start gap-8 pb-14 sm:pb-16 lg:grid-cols-2 lg:gap-12 lg:pb-0 xl:gap-16">
          <div className="fp-feature-copy flex flex-col items-start pb-2 lg:pb-28 xl:pb-32">
            <RichTextContent
              path="floorplanslist.feature_title"
              label="Feature Title"
              defaultValue="<p>Spacious Apartment Homes</p>"
              as="h2"
              className="fp-feature-title-font text-2xl font-normal text-white sm:text-3xl lg:text-[2.75rem]"
            />

            <RichTextContent
              path="floorplanslist.feature_body"
              label="Feature Body Text"
              defaultValue="<p>Discover the allure of our diverse floor plans, ranging from chic studios to expansive three-bedroom residences. Each layout is thoughtfully crafted to provide a harmonious blend of functionality and sophistication.</p>"
              as="div"
              className="fp-feature-body-font mt-5 max-w-xl text-sm leading-relaxed text-white/90 sm:mt-6 sm:text-base lg:mt-8 lg:text-lg"
            />

            <EditableSlotGroup groupPath="floorplanslist.feature_cta" definition={featureCtaDef}>
              <a
                href={resolveHref(featureCta.url || '/floorplans')}
                target={featureCta._target || '_self'}
                className="mt-8 inline-flex items-center justify-center bg-[var(--cta-bg,var(--vibe-color-secondary))] px-8 py-3.5 text-sm font-normal uppercase tracking-widest text-[var(--cta-text,var(--vibe-color-text-on-secondary))] shadow-md transition-colors hover:bg-white hover:text-[var(--cta-bg,var(--vibe-color-secondary))] sm:px-10 sm:py-4 lg:mt-10"
                style={{ ...ctaColorVars, fontFamily: 'var(--vibe-font-heading)' }}
              >
                {featureCta.label || 'Explore Now'}
              </a>
            </EditableSlotGroup>
          </div>

          <div
            data-reveal
            className="fp-reveal-right fp-feature-image relative hidden aspect-[3/2] w-full overflow-hidden shadow-lg lg:block"
          >
            <EditableSlotGroup groupPath="floorplanslist.feature_image" definition={featureImageDef}>
              {featureImage.image ? (
                <VibesitesImage
                  url={featureImage.image}
                  alt={featureImage.alt || 'Property interior'}
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  className="absolute inset-0 h-full w-full object-cover object-center"
                />
              ) : (
                <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
              )}
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        ${onColorLinksCss('.fp-feature-copy [data-vibesite-richtext]')}
        /* Scroll reveal: elements start shifted down + transparent, then slide
           up into place once the observer adds .is-visible. */
        .fp-reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
          will-change: opacity, transform;
        }
        .fp-reveal.is-visible {
          opacity: 1;
          transform: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .fp-reveal {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
        /* Feature image reveal: starts shifted to the right + transparent, then
           slides left into place once the observer adds .is-visible. */
        .fp-reveal-right {
          opacity: 0;
          transform: translateX(60px);
          transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
          will-change: opacity, transform;
        }
        .fp-reveal-right.is-visible {
          opacity: 1;
          transform: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .fp-reveal-right {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
        /* Same Oswald display face as other home-page section titles. */
        .fp-feature-title-font,
        .fp-feature-title-font * {
          font-family: var(--vibe-font-heading, 'Oswald', 'Arial Narrow', system-ui, sans-serif);
          font-weight: 400;
        }
        /* Match the PropertyOverview description typeface (Space Mono, loaded
           globally by the Header). The font-family inherits to the rich-text
           children so the feature body renders in the same face. */
        .fp-feature-body-font,
        .fp-feature-body-font * {
          font-family: var(--vibe-font-body);
        }
        /*
          Angled photo clip. Gentler on small/tablet viewports so the usable
          photo area can hold icons + copy; steeper angles return at lg+.
        */
        .fp-section-image {
          clip-path: polygon(0 0, 100% 7%, 100% 93%, 0 100%);
          -webkit-clip-path: polygon(0 0, 100% 7%, 100% 93%, 0 100%);
        }
        @media (min-width: 768px) {
          .fp-section-image {
            clip-path: polygon(0 0, 100% 11%, 100% 89%, 0 100%);
            -webkit-clip-path: polygon(0 0, 100% 11%, 100% 89%, 0 100%);
          }
        }
        @media (min-width: 1024px) {
          .fp-section-image {
            /* Slightly gentler lower angle so left-column CTA stays on the photo. */
            clip-path: polygon(0 0, 100% 16%, 100% 88%, 0 100%);
            -webkit-clip-path: polygon(0 0, 100% 16%, 100% 88%, 0 100%);
          }
        }
        /* Top safe band ≈ clip inset + badge overhang; scales with viewport. */
        .fp-clip-top-safe {
          height: clamp(5.5rem, 12vw, 9rem);
        }
        @media (min-width: 768px) {
          .fp-clip-top-safe {
            height: clamp(6.5rem, 11vw, 10rem);
          }
        }
        @media (min-width: 1024px) {
          .fp-clip-top-safe {
            height: clamp(8rem, 12vw, 12rem);
          }
        }
        /* Slight overhang past the lower diagonal; image stays top-aligned
           with the feature title so it does not sit at the section floor. */
        .fp-feature-image {
          margin-bottom: -1.25rem;
        }
        @media (min-width: 1024px) {
          .fp-feature-image {
            margin-bottom: -2rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.feature_title': {
      label: 'Feature Title',
      type: 'richtext',
      defaultValue: '<p>Spacious Apartment Homes</p>',
    },
    'floorplanslist.feature_body': {
      label: 'Feature Body Text',
      type: 'richtext',
      defaultField: 'property_home_floorplans_blurb',
      defaultValue:
        '<p>Discover the allure of our diverse floor plans, ranging from chic studios to expansive three-bedroom residences. Each layout is thoughtfully crafted to provide a harmonious blend of functionality and sophistication.</p>',
    },
  },
  slotGroups: {
    'floorplanslist.background': backgroundDef,
    'floorplanslist.feature_image': featureImageDef,
    'floorplanslist.feature_cta': featureCtaDef,
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
